import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo, { buildBreadcrumbSchema, buildArticleSchema } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BlogIndexPage() {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    axios.get(`${API}/seo/blog`).then((r) => setPosts(r.data));
  }, []);
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)]">
      <Seo
        title="Wedding Planning Blog & Resources"
        description="Wedding planning checklists, vow inspiration, honeymoon ideas, reception games, and luxury wedding guides — by LockedInForever."
        path="/blog"
        keywords={[
          "wedding blog",
          "wedding planning checklist",
          "wedding tips",
          "vow inspiration",
          "honeymoon ideas",
        ]}
        schema={buildBreadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "Blog", url: "/blog" },
        ])}
      />
      <Navbar />
      <section className="container-wide section-pad" data-testid="blog-index">
        <p className="font-script text-3xl text-[var(--gold)] mb-3">Resources</p>
        <h1 className="font-serif text-5xl md:text-6xl font-light">The Forever Library</h1>
        <p className="text-black/60 max-w-2xl mt-4">
          Tactical, beautiful, and useful — articles that help couples plan a wedding and a marriage worth remembering.
        </p>

        <div className="grid md:grid-cols-3 gap-8 mt-12">
          {(posts || []).map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              className="border border-black/10 rounded-2xl p-6 bg-white/40 hover:bg-white transition"
              data-testid={`blog-card-${p.slug}`}
            >
              <h2 className="font-serif text-2xl">{p.title}</h2>
              <p className="text-sm text-black/60 mt-3">{p.excerpt}</p>
              <p className="text-xs text-[var(--gold)] uppercase tracking-widest mt-5">Read article →</p>
            </Link>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/seo/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setErr(true));
  }, [slug]);

  if (err) {
    return (
      <div className="min-h-screen bg-[var(--bg)]">
        <Navbar />
        <div className="container-wide section-pad text-center">
          <h1 className="font-serif text-4xl">Article not found</h1>
          <Link to="/blog" className="btn-gold mt-6 inline-block">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }
  if (!post) return null;

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)]">
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${slug}`}
        type="article"
        schema={[
          buildArticleSchema({ title: post.title, description: post.excerpt, slug }),
          buildBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Blog", url: "/blog" },
            { name: post.title, url: `/blog/${slug}` },
          ]),
        ]}
      />
      <Navbar />
      <article className="container-wide section-pad max-w-3xl" data-testid="blog-post">
        {post.cover_url && (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${post.cover_url}`}
            alt={post.title}
            className="w-full aspect-[16/9] object-cover rounded-2xl mb-10"
          />
        )}
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-3">
          {post.author || "LockedInForever Editorial"}
        </p>
        <h1 className="font-serif font-light text-4xl md:text-6xl leading-tight mb-4">
          {post.title}
        </h1>
        {post.excerpt && (
          <p className="text-lg md:text-xl text-black/60 mb-10">{post.excerpt}</p>
        )}
        <div
          className="lif-article font-body text-black/80 text-[17px] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: post.body_html }}
        />
        <Link to="/blog" className="text-sm underline text-[var(--gold)] mt-12 inline-block">
          ← Back to all articles
        </Link>
      </article>
      <Footer />
    </div>
  );
}
